import React, { useState, useEffect } from 'react';
import { FaHourglassHalf, FaTicketAlt, FaEye, FaTimes, FaMapMarkerAlt, FaCalendarAlt, FaBullseye, FaVideo, FaImage, FaLink, FaSearch, FaClock, FaCopy } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DescriptionViewer from '../../Common/DescriptionViewer';

const ApprovedAdvertisements = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedAd, setSelectedAd] = useState(null); // State for Details Modal
  const [searchTerm, setSearchTerm] = useState(''); // State for Searching by ID

  useEffect(() => {
    const fetchPendingAds = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/advertiser/advertisements?status=approved,modified`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        });

        const data = response.data.data || response.data;
        console.log('Fetched Approved Advertisements:', data);
        const approvedAds = Array.isArray(data) ? data.filter(ad => ad.status === 'approved' || ad.status === 'modified') : [];

        setAdvertisements(approvedAds);
        setPendingCount(approvedAds.length);
      } catch (error) {
        console.error('Error fetching pending advertisements:', error);
        toast.error('Failed to load pending advertisements');
      } finally {
        setLoading(false);
      }
    };

    fetchPendingAds();
  }, []);



  return (
    <div className="p-2 sm:p-4 md:p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex flex-col mb-6 gap-4">
        <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">Approved & Modified Ads</h1>

        {/* Floating Pill Bar for Desktop & Mobile Navigation */}
        <div className="bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-200/40 border border-slate-100 rounded-[1.5rem] sm:rounded-full p-1.5 sm:p-2 w-full mx-auto pointer-events-auto transition-all">
          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-1.5 sm:gap-2 w-full">
            {[
              { path: '/advertiser/leads-status/underReview', label: 'Under Review', activeColor: 'bg-yellow-500', activeShadow: 'shadow-yellow-500/30' },
              { path: '/advertiser/leads-status/pending', label: 'Approved', activeColor: 'bg-blue-500', activeShadow: 'shadow-blue-500/30', active: true },
              { path: '/advertiser/leads-status/accepted', label: 'Accepted', activeColor: 'bg-teal-500', activeShadow: 'shadow-teal-500/30' },
              { path: '/advertiser/leads-status/running', label: 'Running', activeColor: 'bg-emerald-500', activeShadow: 'shadow-emerald-500/30' },
              { path: '/advertiser/leads-status/completed', label: 'Completed', activeColor: 'bg-indigo-500', activeShadow: 'shadow-indigo-500/30' },
              { path: '/advertiser/leads-status/rejected', label: 'Rejected', activeColor: 'bg-rose-500', activeShadow: 'shadow-rose-500/30' }
            ].map((item, idx) => (
              <Link
                key={idx}
                to={item.path}
                className={`
                  flex-1 sm:flex-none flex items-center justify-center px-2 sm:px-4 py-2 sm:py-2.5 
                  rounded-xl sm:rounded-full transition-all duration-300 text-[10px] sm:text-xs font-black uppercase tracking-widest text-center whitespace-nowrap
                  ${item.active
                    ? `${item.activeColor} text-white shadow-md ${item.activeShadow} scale-[1.02]`
                    : 'bg-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700 active:scale-95'
                  }
                `}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Card */}
      <div className="mb-4 sm:mb-8">
        <div className="bg-yellow-500 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl shadow-lg text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs sm:text-sm font-medium opacity-80">Approved & Modified Ads</p>
              <p className="text-xl sm:text-2xl font-bold mt-1">{pendingCount}</p>
            </div>
            <FaHourglassHalf className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 opacity-80" />
          </div>
        </div>
      </div>

      {/* Advertisement Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      ) : (
        <>
          {/* Search Bar */}
          <div className="mb-4 sm:mb-6">
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                placeholder="Search by Advertisement ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 sm:py-3.5 rounded-2xl border-2 border-slate-100 bg-white focus:outline-none focus:ring-4 focus:ring-yellow-500/10 focus:border-yellow-500 transition-all text-sm sm:text-base shadow-sm font-medium text-slate-700 placeholder:text-slate-400"
              />
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            </div>
          </div>

          {/* Responsive List View (Vertical Stacked Cards for Desktop & Mobile) */}
          <div className="flex flex-col gap-3 sm:gap-4 mb-4">
            {advertisements
              .filter(ad => {
                if (!searchTerm) return true;
                const searchStr = searchTerm.toLowerCase();
                return ad._id?.toLowerCase().includes(searchStr) ||
                  ad.adId?.toLowerCase().includes(searchStr);
              })
              .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
              .map((ad) => {
                // Format Date safely
                let formattedDate = "N/A";
                if (ad.createdAt) {
                  const dateObj = new Date(ad.createdAt);
                  if (!isNaN(dateObj)) {
                    // Extracting the Date (e.g., Oct 24, 2024)
                    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
                    formattedDate = dateObj.toLocaleDateString('en-US', options);
                  }
                }

                return (
                  <div key={ad._id || ad.id} className="bg-white p-3 sm:p-4 rounded-2xl shadow-sm border border-slate-100 flex gap-4 relative hover:shadow-md transition-shadow items-center">

                    {/* Left Side Media */}
                    <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-xl overflow-hidden shrink-0 bg-slate-50 border border-slate-100 shadow-inner flex items-center justify-center relative group">
                      {ad.mediaType === 'image' && ad.imageUrl ? (
                        <img src={ad.imageUrl} alt={ad.title} className="w-full h-full object-cover" />
                      ) : ad.mediaType === 'video' && ad.videoUrl ? (
                        <video src={ad.videoUrl} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[10px] sm:text-xs text-gray-400">No media</span>
                      )}
                      <div className="absolute top-1 left-1 bg-black/60 backdrop-blur-sm text-white text-[8px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                        {ad.mediaType}
                      </div>
                    </div>

                    {/* Right Side Content (Flex Row on PC, Flex Col on Mobile) */}
                    <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-6">

                      {/* Primary Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 px-2 py-0.5 rounded-md w-max group/id">
                            <p className="text-[9px] sm:text-[10px] font-black text-slate-500 font-mono tracking-wider uppercase">#{ad._id}</p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(ad._id);
                                toast.success('ID Copied!');
                              }}
                              className="text-slate-400 hover:text-indigo-600 transition-colors p-0.5"
                              title="Copy ID"
                            >
                              <FaCopy className="w-3 h-3 group-hover/id:scale-110 transition-transform" />
                            </button>
                          </div>
                          <span className={`shrink-0 text-[8px] sm:text-[10px] font-black px-2 py-0.5 rounded-md uppercase tracking-widest leading-none border flex items-center gap-1 ${ad.status === 'modified'
                            ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                            : 'bg-blue-50 text-blue-600 border-blue-100'
                            }`}>
                            {ad.status === 'modified' ? 'Modified' : 'Approved'}
                          </span>
                          <span className="shrink-0 text-[8px] sm:text-[10px] font-bold text-slate-500 flex items-center gap-1 ml-auto sm:ml-2">
                            <FaClock className="text-slate-400" /> {formattedDate}
                          </span>
                        </div>

                        <p className="text-xs sm:text-sm font-bold text-slate-800 line-clamp-2 leading-tight mb-2 sm:mb-3">
                          {ad.mediaDescription || "No description provided."}
                        </p>

                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[10px] sm:text-xs font-black bg-emerald-50 text-emerald-600 px-2 sm:px-2.5 py-1 rounded-lg border border-emerald-100">
                            ₹{Number(ad.totalCost).toFixed(2)}
                          </span>
                          <span className="text-[10px] sm:text-xs font-bold bg-slate-50 text-slate-600 px-2 sm:px-2.5 py-1 rounded-lg border border-slate-100 capitalize flex items-center gap-1">
                            {ad.userType === 'influencer' ? '🎯' : '📰'} {ad.userType}
                          </span>
                          <span className="text-[10px] sm:text-xs font-bold bg-blue-50 text-blue-600 px-2 sm:px-2.5 py-1 rounded-lg border border-blue-100 capitalize">
                            {ad.requiredViews} Views
                          </span>
                        </div>

                        {ad.couponCode && (
                          <div className="mt-2 flex items-center gap-1">
                            <FaTicketAlt className="text-[10px] text-orange-400" />
                            <span className="text-[9px] sm:text-[10px] font-black text-orange-600 uppercase tracking-widest">{ad.couponCode} applied</span>
                          </div>
                        )}
                      </div>

                      {/* Actions / View Details Button */}
                      <div className="shrink-0 flex sm:flex-col items-center justify-end sm:justify-center border-t sm:border-t-0 sm:border-l border-slate-100 pt-2 sm:pt-0 sm:pl-6">
                        <button
                          onClick={() => setSelectedAd(ad)}
                          className="w-full sm:w-auto px-4 py-2 sm:py-3 rounded-xl bg-indigo-50 flex items-center justify-center gap-2 text-indigo-600 hover:bg-indigo-600 hover:text-white hover:shadow-lg hover:shadow-indigo-200 transition-all group"
                        >
                          <span className="font-bold text-xs sm:text-sm uppercase tracking-wider">Details</span>
                          <FaEye className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        </button>
                      </div>

                    </div>
                  </div>
                );
              })}

            {/* Show empty state if nothing matches search but list is not entirely empty overall */}
            {advertisements.length > 0 && advertisements.filter(ad => {
              if (!searchTerm) return true;
              const searchStr = searchTerm.toLowerCase();
              return ad._id?.toLowerCase().includes(searchStr) ||
                ad.adId?.toLowerCase().includes(searchStr);
            }).length === 0 && (
                <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 text-center text-slate-500">
                  No advertisements found matching "{searchTerm}"
                </div>
              )}
            {/* Show completely empty state if NO advertisements exist */}
            {advertisements.length === 0 && (
              <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 text-center">
                <FaHourglassHalf className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto text-gray-300 mb-2 sm:mb-3 md:mb-4" />
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-2">No Pending/Modified Advertisements</h3>
                <p className="text-xs sm:text-sm md:text-base text-gray-500">You don't have any pending advertisements at the moment.</p>
              </div>
            )}
          </div>

          {/* End of Advertisements Grid */}
        </>
      )}

      {/* Full Details Modal - Complete Information Rendering */}
      {selectedAd && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
            onClick={() => setSelectedAd(null)}
          ></div>
          <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh] animate-slide-up">

            {/* Modal Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-slate-100 bg-slate-50/50">
              <div>
                <h2 className="text-xl font-black text-slate-800 tracking-tight">Campaign Details</h2>
                <p className="text-xs font-bold text-slate-400 font-mono tracking-tighter mt-0.5">ID: {selectedAd._id}</p>
              </div>
              <button
                onClick={() => setSelectedAd(null)}
                className="p-2 bg-white text-slate-400 hover:text-slate-800 rounded-full shadow-sm border border-slate-100 transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Layout - Scrollable Body */}
            <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar flex flex-col gap-6">

              {/* Top Status Banner */}
              <div className={`flex flex-wrap md:flex-nowrap gap-3 items-center justify-between p-4 rounded-2xl border w-full ${selectedAd.status === 'modified' ? 'bg-emerald-50 border-emerald-100' : 'bg-blue-50 border-blue-100'
                }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${selectedAd.status === 'modified' ? 'bg-emerald-100' : 'bg-blue-100'
                    }`}>
                    <FaHourglassHalf className={`text-lg ${selectedAd.status === 'modified' ? 'text-emerald-600' : 'text-blue-600'}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-black tracking-tight ${selectedAd.status === 'modified' ? 'text-emerald-800' : 'text-blue-800'}`}>
                      {selectedAd.status === 'modified' ? 'Targeting Modified' : 'Approved'}
                    </p>
                    <p className={`text-[10px] font-bold uppercase tracking-widest ${selectedAd.status === 'modified' ? 'text-emerald-600' : 'text-blue-600'}`}>
                      Admin Checked
                    </p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-[10px] font-bold uppercase tracking-widest ${selectedAd.status === 'modified' ? 'text-emerald-600' : 'text-blue-600'}`}>Created On</p>
                  <p className={`text-xs font-black ${selectedAd.status === 'modified' ? 'text-emerald-800' : 'text-blue-800'}`}>{new Date(selectedAd.createdAt).toLocaleDateString()} at {new Date(selectedAd.createdAt).toLocaleTimeString()}</p>
                </div>
              </div>

              {/* Two Column Layout for details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Left Column: Media & Core Details */}
                <div className="space-y-6">
                  {/* Media Preview */}
                  <div>
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                      {selectedAd.mediaType === 'image' ? <FaImage /> : <FaVideo />} Ad Media ({selectedAd.mediaType})
                    </h3>
                    <div className="w-full bg-slate-900 rounded-2xl overflow-hidden aspect-video relative shadow-inner border border-slate-200">
                      {selectedAd.mediaType === 'image' ? (
                        <img src={selectedAd.imageUrl} alt={selectedAd.title} className="w-full h-full object-contain" />
                      ) : (
                        <video src={selectedAd.videoUrl} controls className="w-full h-full object-contain" />
                      )}
                    </div>
                  </div>

                  {/* Description Block */}
                  <div>
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-100 pb-2">
                      Description Provided
                    </h3>
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                      <p className="text-sm text-slate-700 font-medium whitespace-pre-wrap">{selectedAd.mediaDescription || "No description provided."}</p>
                    </div>
                  </div>
                </div>

                {/* Right Column: Deep Stats */}
                <div className="space-y-6">

                  {/* Targets & Finance */}
                  <div>
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                      <FaBullseye /> Campaign Targets & Finance
                    </h3>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      <div className="p-3 bg-blue-50 border border-blue-100 rounded-2xl">
                        <p className="text-[9px] font-bold text-blue-500 uppercase tracking-widest">Target Users</p>
                        <p className="text-sm font-black text-blue-700 capitalize flex items-center gap-1 mt-0.5">
                          {selectedAd.userType === 'influencer' ? '🎯' : '📰'} {selectedAd.userType}s
                        </p>
                      </div>
                      <div className="p-3 bg-violet-50 border border-violet-100 rounded-2xl">
                        <p className="text-[9px] font-bold text-violet-500 uppercase tracking-widest">Required Views</p>
                        <p className="text-sm font-black text-violet-700 flex items-center gap-1 mt-0.5">
                          <FaEye className="text-violet-400" /> {selectedAd.requiredViews}
                        </p>
                      </div>
                      <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-2xl">
                        <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest">Subtotal</p>
                        <p className="text-sm font-black text-emerald-700 flex items-center gap-1 mt-0.5">
                          ₹{Number(selectedAd.subtotal || 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-2xl">
                        <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest">GST (Platform)</p>
                        <p className="text-sm font-black text-emerald-700 flex items-center gap-1 mt-0.5">
                          ₹{Number(selectedAd.gst || 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="p-3 bg-emerald-100 border border-emerald-200 rounded-2xl col-span-2 lg:col-span-4">
                        <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest text-center">Final Paid Amount</p>
                        <p className="text-lg font-black text-emerald-800 flex justify-center items-center gap-1 mt-0.5">
                          ₹{Number(selectedAd.totalCost || 0).toFixed(2)}
                        </p>
                      </div>

                      {selectedAd.couponCode && (
                        <div className="p-3 bg-orange-50 border border-orange-100 rounded-2xl col-span-2 lg:col-span-4">
                          <p className="text-[9px] font-bold text-orange-500 uppercase tracking-widest">Coupon Used</p>
                          <p className="text-sm font-black text-orange-700 flex items-center gap-1 mt-0.5">
                            <FaTicketAlt className="text-orange-400" /> {selectedAd.couponCode}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Demographics Area */}
                  <div>
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                      <FaMapMarkerAlt /> Geographical Targeting
                    </h3>
                    <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl space-y-3">
                      <div>
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">State Level</p>
                        <span className="inline-block px-3 py-1 bg-white border border-slate-200 shadow-sm rounded-lg text-xs font-bold text-slate-700">
                          {selectedAd.pfState || "All States"}
                        </span>
                      </div>
                      <div>
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Specific Cities</p>
                        <div className="flex flex-wrap gap-1.5">
                          {selectedAd.pfCities && selectedAd.pfCities.length > 0 ? (
                            selectedAd.pfCities.map(city => (
                              <span key={city} className="inline-block px-2.5 py-0.5 bg-white border border-slate-200 shadow-sm rounded-md text-[10px] font-black text-slate-600">
                                {city}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs font-semibold text-slate-400">No specific cities</span>
                          )}
                        </div>
                      </div>

                      {/* Admin Modified Targeting */}
                      {(selectedAd.adminSelectState || (selectedAd.adminSelectCities && selectedAd.adminSelectCities.length > 0)) && (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                            <FaBullseye /> Modified Targeting (Admin)
                          </p>
                          <div className="space-y-3">
                            <div>
                              <p className="text-[9px] font-bold text-blue-400 uppercase tracking-widest mb-1">State</p>
                              <span className="inline-block px-3 py-1 bg-blue-50 border border-blue-100 shadow-sm rounded-lg text-xs font-bold text-blue-700">
                                {selectedAd.adminSelectState || "Multi-State"}
                              </span>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold text-blue-400 uppercase tracking-widest mb-1">Specific Cities</p>
                              <div className="flex flex-wrap gap-1.5">
                                {selectedAd.adminSelectCities && selectedAd.adminSelectCities.length > 0 ? (
                                  selectedAd.adminSelectCities.map(city => (
                                    <span key={city} className="inline-block px-2.5 py-0.5 bg-blue-50 border border-blue-100 shadow-sm rounded-md text-[10px] font-black text-blue-600">
                                      {city}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs font-semibold text-blue-400">No specific cities modified</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                    </div>
                  </div>

                  {/* Timelines */}
                  <div>
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                      <FaCalendarAlt /> Campaign Schedule
                    </h3>
                    <div className="flex gap-4">
                      <div className="flex-1 p-3 bg-slate-50 border border-slate-100 rounded-2xl text-center">
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Start Date</p>
                        <p className="text-xs font-black text-slate-800">{selectedAd.startDate || "N/A"}</p>
                      </div>
                      <div className="flex-1 p-3 bg-slate-50 border border-slate-100 rounded-2xl text-center">
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">End Date</p>
                        <p className="text-xs font-black text-slate-800">{selectedAd.endDate || "N/A"}</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Extra Campaign Settings block */}
              <div>
                <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                  <FaVideo /> Advertisement Configurations
                </h3>
                <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Ad Format</p>
                    <p className="text-sm font-black text-slate-800 capitalize">{selectedAd.adType || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Length</p>
                    <p className="text-sm font-black text-slate-800">{selectedAd.adLength || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Req Reporters</p>
                    <p className="text-sm font-black text-slate-800">{selectedAd.requiredReporter !== undefined ? selectedAd.requiredReporter : "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Platforms</p>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {selectedAd.platforms && selectedAd.platforms.length > 0 ? selectedAd.platforms.map((pf, idx) => (
                        <span key={idx} className="px-1.5 py-0.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded text-[9px] font-bold uppercase">{pf}</span>
                      )) : <span className="text-xs font-bold text-slate-400">Any</span>}
                    </div>
                  </div>
                  {selectedAd.channelType && selectedAd.channelType.length > 0 && (
                    <div className="col-span-2 sm:col-span-4 mt-2">
                      <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Target Channel Types</p>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {selectedAd.channelType.map((type, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-600 border border-blue-100 rounded-md text-[10px] font-black">{type}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Any extra optional URL attachments */}
              {selectedAd.targetUrl && (
                <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex items-center justify-between gap-4 mt-2">
                  <div className="flex items-center gap-3 w-full">
                    <FaLink className="text-blue-500 text-xl shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-0.5">Attached Target Link</p>
                      <a href={selectedAd.targetUrl} target="_blank" rel="noreferrer" className="text-sm font-black text-blue-700 hover:underline truncate block w-full">
                        {selectedAd.targetUrl}
                      </a>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovedAdvertisements;
